import { useCallback } from 'react';
import { useCurrentLanguage } from 'tradera/lang/translate';
import { useAppSelector } from 'tradera/state/hooks';
import { selectPreferredCurrency } from 'tradera/state/multi-currency/selectors';
import type {
    OverrideFormatPriceOptions,
    Price
} from 'tradera/localization/format-price';
import { formatPrice } from 'tradera/localization/format-price';

/**
 * Formats a price in the current language and currency.
 */
export const useLocalizedPriceFormatter = () => _useLocalizedPriceFormatter();

/**
 * Same as useLocalizedPriceFormatter but it always rounds up. Needed for fixed prices,
 * so we never display a price that is lower than what the user will pay in the end.
 */
export const useLocalizedPriceFormatterRoundingUp = () =>
    _useLocalizedPriceFormatter({ roundUp: true });

/**
 * Does not convert anything. If you for some reason are working with the local currency
 * it could be used but avoid it. Example of usage: in the bid-modal we want to display
 * the exact amount that was input
 */
export const useLocalizedPriceFormatterNoConversion = () =>
    _useLocalizedPriceFormatter({ conversion: false });

/**
 * Makes the conversion but leaves the symbol. Used to compare local amounts to make a
 * decision in the code.
 */
export const useLocalizedPriceFormatterNoSymbol = () =>
    _useLocalizedPriceFormatter({ style: 'decimal' });

/**
 * Formats a price in the current language and currency but forces the value to be
 * rendered using up to two decimals, even for currencies which are normally without
 * decimals.
 */
export const useLocalizedPriceFormatterAllowFractions = () =>
    _useLocalizedPriceFormatter({ allowFractions: true });

type LocalizedPriceFormatterOptions = {
    allowFractions?: boolean;
    conversion?: boolean;
    roundUp?: OverrideFormatPriceOptions['roundUp'];
    style?: OverrideFormatPriceOptions['style'];
};

/**
 * Internal to this module, needed to limit the scope of the API.
 */
const _useLocalizedPriceFormatter = ({
    conversion = true,
    style,
    roundUp,
    allowFractions
}: LocalizedPriceFormatterOptions = {}) => {
    const { language } = useCurrentLanguage();
    const preferredCurrency = useAppSelector(selectPreferredCurrency);

    return useCallback(
        (price: Price) =>
            formatPrice(
                conversion ? preferredCurrency.rate * Number(price) : price,
                language,
                {
                    currency: preferredCurrency.code,
                    minimumFractionDigits: preferredCurrency.decimals,
                    maximumFractionDigits: allowFractions
                        ? 2
                        : preferredCurrency.decimals,
                    ...(style ? { style } : {}),
                    ...(roundUp ? { roundUp } : {})
                }
            ),
        [
            conversion,
            language,
            preferredCurrency,
            roundUp,
            style,
            allowFractions
        ]
    );
};
